import { create } from 'zustand';
import type { Site } from '@mawkaa/types';
import { api } from '@/lib/api';

interface BrandSettings {
  colors?: { primary: string; secondary: string; accent: string };
  fonts?: { heading: string; body: string };
  logo?: string;
}

interface AIGenerateSiteData {
  name: string;
  subdomain: string;
  settings: {
    colors: {
      primary: string;
      secondary: string;
      accent: string;
      background?: string;
      text?: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
  };
  pages: Array<{
    name: string;
    slug: string;
    isHomepage: boolean;
    blocks: Array<{
      type: string;
      content: Record<string, unknown>;
      settings?: Record<string, unknown>;
    }>;
  }>;
  businessInfo?: {
    name?: string;
    type?: string;
    description?: string;
  };
}

interface SitesState {
  sites: Site[];
  isLoading: boolean;

  // Actions
  fetchSites: () => Promise<void>;
  createSite: (
    name: string,
    subdomain: string,
    templateId?: string,
    brandSettings?: BrandSettings
  ) => Promise<Site>;
  createSiteFromAI: (data: AIGenerateSiteData) => Promise<Site>;
  updateSite: (siteId: string, data: Partial<Site>) => Promise<void>;
  deleteSite: (siteId: string) => Promise<void>;
  checkSubdomain: (subdomain: string) => Promise<boolean>;
  publishSite: (siteId: string) => Promise<{ url: string }>;
  unpublishSite: (siteId: string) => Promise<void>;
}

export const useSitesStore = create<SitesState>((set, get) => ({
  sites: [],
  isLoading: false,

  fetchSites: async () => {
    set({ isLoading: true });
    try {
      const response = await api.get<{ sites: Site[] }>('/sites');
      set({ sites: response.sites });
    } finally {
      set({ isLoading: false });
    }
  },

  createSite: async (
    name: string,
    subdomain: string,
    templateId?: string,
    brandSettings?: BrandSettings
  ) => {
    const response = await api.post<{ site: Site }>('/sites', {
      name,
      subdomain,
      templateId,
      settings: brandSettings
        ? {
            colors: brandSettings.colors,
            fonts: brandSettings.fonts,
            logo: brandSettings.logo,
          }
        : undefined,
    });

    set((state) => ({
      sites: [response.site, ...state.sites],
    }));

    return response.site;
  },

  createSiteFromAI: async (data: AIGenerateSiteData) => {
    const response = await api.post<{ site: Site }>('/sites/ai-generate', data);

    set((state) => ({
      sites: [response.site, ...state.sites],
    }));

    return response.site;
  },

  updateSite: async (siteId: string, data: Partial<Site>) => {
    const response = await api.patch<{ site: Site }>(`/sites/${siteId}`, data);

    set((state) => ({
      sites: state.sites.map((site) => (site.id === siteId ? response.site : site)),
    }));
  },

  deleteSite: async (siteId: string) => {
    await api.delete(`/sites/${siteId}`);

    set((state) => ({
      sites: state.sites.filter((site) => site.id !== siteId),
    }));
  },

  checkSubdomain: async (subdomain: string) => {
    const response = await api.get<{ available: boolean }>(`/sites/check-subdomain/${subdomain}`);
    return response.available;
  },

  publishSite: async (siteId: string) => {
    const response = await api.post<{ site: Site; url: string }>(`/sites/${siteId}/publish`);

    set((state) => ({
      sites: state.sites.map((site) => (site.id === siteId ? response.site : site)),
    }));

    return { url: response.url };
  },

  unpublishSite: async (siteId: string) => {
    const response = await api.post<{ site: Site }>(`/sites/${siteId}/unpublish`);

    set((state) => ({
      sites: state.sites.map((site) => (site.id === siteId ? { ...site, status: 'draft' } : site)),
    }));
  },
}));
